function record(value: unknown, context: string): Record<string, unknown> {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        throw new Error(`Invalid ${context} object.`);
    }
    return value as Record<string, unknown>;
}

function knownFields(value: Record<string, unknown>, fields: readonly string[], context: string): void {
    for (const key of Object.keys(value)) {
        if (!fields.includes(key)) throw new Error(`Unknown ${context} field '${key}'.`);
    }
}

function identifier(value: unknown, context: string): string {
    if (typeof value !== 'string' || !/^[a-z][a-z0-9-]*$/.test(value)) {
        throw new Error(`Invalid ${context} id.`);
    }
    return value;
}

function uniquePhrase(text: string, offset: number, pinned: unknown, id: string): string {
    const matchesPosition = (phrase: string) => text.indexOf(phrase) === offset
        && text.indexOf(phrase, offset + 1) === -1;
    if (pinned !== undefined) {
        if (typeof pinned !== 'string' || !pinned.trim() || !matchesPosition(pinned)) {
            throw new Error(`Inline cue '${id}' requires a unique beforeText phrase at its text position.`);
        }
        return pinned;
    }
    // Grow through complete words; a repeated suffix cannot be represented by phrase-based cues.
    for (const word of text.slice(offset).matchAll(/\S+/g)) {
        const phrase = text.slice(offset, offset + word.index + word[0].length);
        if (matchesPosition(phrase)) return phrase;
    }
    throw new Error(`Inline cue '${id}' has no unique narration phrase at its text position.`);
}

/** Compiles source-only inline direction; adapter parsers still validate the resulting visual payloads. */
export function normalizeAuthoredBeat(value: unknown, kind: 'concept' | 'trace'): Record<string, unknown> {
    const beat = record(value, 'episode beat');
    if (!Array.isArray(beat.explanation)) return beat;
    if (!beat.explanation.length) throw new Error('Inline explanation requires fragments.');
    if (beat.cues !== undefined || beat.presentation !== undefined || beat.narrationPauses !== undefined || beat.narrationTextMap !== undefined) {
        throw new Error('Inline explanation cannot also declare beat cues or presentation.');
    }
    let initialPresentation: Record<string, unknown> | undefined;
    let pauseBeforeMs: number | undefined;
    const fragments = beat.explanation.map((value: unknown, index: number): string | (Record<string, unknown> & { text: string; spokenText?: string }) => {
        if (typeof value === 'string') return value;
        const fragment = record(value, 'explanation fragment');
        if ('initialPresentation' in fragment) {
            knownFields(fragment, ['initialPresentation', 'pauseBeforeMs'], 'initial direction');
            if (index !== 0) throw new Error('Initial presentation must be the first explanation entry, at most once.');
            initialPresentation = record(fragment.initialPresentation, 'initial presentation');
            if (fragment.pauseBeforeMs !== undefined) {
                if (typeof fragment.pauseBeforeMs !== 'number' || !Number.isInteger(fragment.pauseBeforeMs)
                    || fragment.pauseBeforeMs < 1 || fragment.pauseBeforeMs > 10000) {
                    throw new Error('Inline pauseBeforeMs must be an integer from 1 through 10000.');
                }
                pauseBeforeMs = fragment.pauseBeforeMs;
            }
            return '';
        }
        knownFields(fragment, ['id', 'text', 'spokenText', 'beforeText', 'presentation', 'pauseAfterMs',
            ...(kind === 'trace' ? ['landmark', 'phase'] : [])], 'explanation fragment');
        if (typeof fragment.text !== 'string' || !fragment.text.trim()) {
            throw new Error('Annotated explanation fragments require nonempty text.');
        }
        if (fragment.spokenText !== undefined && (typeof fragment.spokenText !== 'string' || !fragment.spokenText.trim())) {
            throw new Error('Fragment spokenText requires nonempty text.');
        }
        return { ...fragment, text: fragment.text,
            ...(fragment.spokenText === undefined ? {} : { spokenText: fragment.spokenText }) };
    });
    const explanation = fragments.map(fragment => typeof fragment === 'string' ? fragment : fragment.text).join('');
    const spokenText = fragments.map(fragment => typeof fragment === 'string' ? fragment : fragment.spokenText ?? fragment.text).join('');
    if (!explanation.trim()) throw new Error('Inline explanation requires nonempty text.');
    if (beat.spokenText !== undefined && beat.spokenText !== spokenText) {
        throw new Error('Inline explanation requires spokenText to match the concatenated fragment speech exactly.');
    }
    const cues: Record<string, unknown>[] = [];
    const narrationPauses: { offset: number; milliseconds: number }[] =
        pauseBeforeMs === undefined ? [] : [{ offset: 0, milliseconds: pauseBeforeMs }];
    const presentation: Record<string, unknown>[] = [];
    const ids = new Set<string>();
    const presentationIds = new Set<string>();
    function direction(value: unknown, defaultId?: string): Record<string, unknown> {
        const raw = record(value, 'inline presentation');
        knownFields(raw, ['id', 'effects', 'pointerPlacement', 'textPanel', 'cameraView',
            ...(kind === 'concept' ? ['arrangement', 'visualSource'] : ['until'])], 'inline presentation');
        const id = identifier(raw.id === undefined ? defaultId : raw.id, 'inline presentation');
        if (presentationIds.has(id)) throw new Error(`Duplicate inline presentation '${id}'.`);
        presentationIds.add(id);
        if (!Array.isArray(raw.effects)) throw new Error(`Inline presentation '${id}' requires effects.`);
        return { ...raw, id };
    }
    if (initialPresentation) presentation.push(direction(initialPresentation));
    let offset = 0;
    let displayOffset = 0;
    const narrationTextMap = [{ spokenOffset: 0, displayOffset: 0 }];
    for (const fragment of fragments) {
        if (typeof fragment === 'string') {
            offset += fragment.length;
            displayOffset += fragment.length;
            if (fragment.length) narrationTextMap.push({ spokenOffset: offset, displayOffset });
            continue;
        }
        const id = identifier(fragment.id, 'inline cue');
        if (ids.has(id)) throw new Error(`Duplicate inline cue '${id}'.`);
        ids.add(id);
        if (/\s/.test(spokenText[offset]) || (offset > 0 && !/\s/.test(spokenText[offset - 1]))) {
            throw new Error(`Inline cue '${id}' must begin a word, without leading whitespace.`);
        }
        const beforeText = uniquePhrase(spokenText, offset, fragment.beforeText, id);
        if (kind === 'trace') {
            if (typeof fragment.landmark !== 'string' || !fragment.landmark.trim()
                || (fragment.phase !== undefined && fragment.phase !== 'start' && fragment.phase !== 'complete')) {
                throw new Error(`Inline trace cue '${id}' requires a landmark and a valid phase.`);
            }
        }
        cues.push({ id, beforeText, ...(kind === 'trace' ? {
            landmark: fragment.landmark, ...(fragment.phase === undefined ? {} : { phase: fragment.phase }),
        } : {}) });
        if (fragment.presentation !== undefined) {
            presentation.push({ ...direction(fragment.presentation, id), afterCue: id });
        }
        offset += (fragment.spokenText ?? fragment.text).length;
        displayOffset += fragment.text.length;
        narrationTextMap.push({ spokenOffset: offset, displayOffset });
        if (fragment.pauseAfterMs !== undefined) {
            if (typeof fragment.pauseAfterMs !== 'number' || !Number.isInteger(fragment.pauseAfterMs)
                || fragment.pauseAfterMs <= 0 || fragment.pauseAfterMs > 10000) {
                throw new Error('Inline pauseAfterMs must be an integer from 1 through 10000.');
            }
            narrationPauses.push({ offset, milliseconds: fragment.pauseAfterMs });
        }
    }
    return { ...beat, explanation, ...(cues.length ? { cues } : {}),
        ...(spokenText !== explanation ? { spokenText, narrationTextMap } : {}),
        ...(narrationPauses.length ? { narrationPauses } : {}),
        ...(presentation.length ? { presentation } : {}) };
}

export function parseNarrationTextMap(value: unknown, spokenText: string, explanation: string):
    readonly { spokenOffset: number; displayOffset: number }[] {
    if (!Array.isArray(value) || value.length < 2) throw new Error('Narration text mapping requires start and end boundaries.');
    let spoken = -1, display = -1;
    const map = value.map(entry => {
        const point = record(entry, 'narration text mapping');
        knownFields(point, ['spokenOffset', 'displayOffset'], 'narration text mapping');
        if (typeof point.spokenOffset !== 'number' || !Number.isInteger(point.spokenOffset)
            || point.spokenOffset <= spoken || point.spokenOffset > spokenText.length
            || typeof point.displayOffset !== 'number' || !Number.isInteger(point.displayOffset)
            || point.displayOffset <= display || point.displayOffset > explanation.length) {
            throw new Error('Narration text mapping requires strictly increasing in-range text offsets.');
        }
        spoken = point.spokenOffset;
        display = point.displayOffset;
        return { spokenOffset: spoken, displayOffset: display };
    });
    if (map[0]!.spokenOffset !== 0 || map[0]!.displayOffset !== 0
        || spoken !== spokenText.length || display !== explanation.length) {
        throw new Error('Narration text mapping must cover both complete texts.');
    }
    return map;
}

export function parseNarrationPauses(value: unknown, text: string): readonly { offset: number; milliseconds: number }[] {
    if (!Array.isArray(value) || !value.length) throw new Error('Narration pauses require a nonempty array.');
    let previous = -1;
    return value.map(entry => {
        const pause = record(entry, 'narration pause');
        knownFields(pause, ['offset', 'milliseconds'], 'narration pause');
        if (typeof pause.offset !== 'number' || !Number.isInteger(pause.offset) || pause.offset <= previous
            || pause.offset > text.length || (pause.offset > 0 && pause.offset < text.length
                && !/\s/.test(text[pause.offset - 1]!) && !/\s/.test(text[pause.offset]!))
            || typeof pause.milliseconds !== 'number' || !Number.isInteger(pause.milliseconds)
            || pause.milliseconds < 1 || pause.milliseconds > 10000) {
            throw new Error('Narration pauses require ordered word boundaries and integer milliseconds from 1 through 10000.');
        }
        previous = pause.offset;
        return { offset: pause.offset, milliseconds: pause.milliseconds };
    });
}
